import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { SortDescription } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { CellType, DataMap, FormatItemEventArgs } from '@grapecity/wijmo.grid'

import { BaseComponent, FeatureType, Grid, ManpowerMetricExclusion, ModalSize } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { ManpowerOutlierComponent } from './manpower-outlier.component'

@Component({
    templateUrl: './manpower-outliers.component.html',
    styleUrls: ['./manpower-outliers.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class ManpowerOutliersComponent extends BaseComponent {
    public grid: Grid<ManpowerMetricExclusion>
    userMap = new DataMap([], 'id', 'displayName')

    constructor() {
        super()
        this.grid = new Grid<ManpowerMetricExclusion>(this.services)
            .setupModalEditing(
                this.services.data.manpowerMetricExclusions,
                'Manpower Outlier',
                ManpowerOutlierComponent,
                () => new ManpowerMetricExclusion(),
                this,
                ModalSize.medium,
            )
            .filterBy(item => [item.job, item.jobDescription, item.jobDisplay, item.reason])
            .wrapColumnHeaders()

        this.grid.canEdit = this.canEdit
        this.grid.itemEdited.on(() => this.refresh())
        this.refresh()
    }

    public get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.OperationsManpowerOutliersEdit])
    }

    public refresh() {
        this.grid.canEdit = this.canEdit
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get<ManpowerMetricExclusion[]>('api/ManpowerMetricExclusion/GetCandidates'),
                    this.services.data.get<any[]>('api/User/GetForLookup'),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        const rows = (result[0] ?? []).map(row => this.prepareRow(row))
                        this.userMap = new DataMap(result[1] ?? [], 'id', 'displayName')
                        this.grid.items = rows
                        this.grid.view.sortDescriptions.clear()
                        this.grid.view.sortDescriptions.push(new SortDescription('pointsAdded', false))
                    },
                }),
        )
    }

    public onFormatItem(event: FormatItemEventArgs) {
        if (event.panel.cellType !== CellType.Cell) {
            return
        }

        const item = event.panel.rows[event.row]?.dataItem as ManpowerMetricExclusion | undefined
        event.cell.classList.toggle('manpower-outlier-suggested', !!item?.suggested)
    }

    private prepareRow(row: ManpowerMetricExclusion): ManpowerMetricExclusion {
        const emptyId = '00000000-0000-0000-0000-000000000000'
        if (!row.createdByUserId || row.createdByUserId === emptyId) {
            row.createdByUserId = null
        }
        if (row.hasExclusion && row.effectiveOn) {
            row.effectiveOn = new Date(row.effectiveOn)
        } else {
            row.effectiveOn = null
        }
        const job = (row.job ?? '').trim()
        const description = (row.jobDescription ?? '').trim()
        row.jobDisplay = description ? `${job} ${description}` : job
        return row
    }
}
